import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { FiArrowRight, FiArrowLeft, FiCheck, FiUser, FiMail, FiLock, FiMapPin, FiClock, FiSettings } from 'react-icons/fi';
import SpinningSun from '../../components/common/SpinningSun';
import './InviteAcceptance.css';

const InviteAcceptance = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  // Form state
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    displayName: '',
    password: '',
    confirmPassword: '',
    phone: '',
    timezone: 'America/Los_Angeles',
    businessName: '',
    businessAddress: '',
    preferredContact: 'email',
    workingHours: {
      start: '09:00',
      end: '17:00'
    }
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [validatingToken, setValidatingToken] = useState(true);

  // Validate token on component mount
  useEffect(() => {
    const validateInvitation = async () => {
      try {
        setValidatingToken(true);
        
        const userOperations = httpsCallable(functions, 'userOperations');
        const result = await userOperations({ operation: 'validateInvitationToken', data: { token } });
        
        console.log('DEBUG FRONTEND - Full result:', JSON.stringify(result, null, 2));
        console.log('DEBUG FRONTEND - result.data:', JSON.stringify(result.data, null, 2));
        console.log('DEBUG FRONTEND - result.data.success:', result.data.success);
        console.log('DEBUG FRONTEND - result.data.result:', JSON.stringify(result.data.result, null, 2));
        console.log('DEBUG FRONTEND - result.data.result.data:', JSON.stringify(result.data.result?.data, null, 2));
        
        if (result.data.success && result.data.result?.success && result.data.result?.data) {
          setInvitation(result.data.result.data);
          setFormData(prev => ({
            ...prev,
            displayName: result.data.result.data.displayName || ''
          }));
        } else {
          throw new Error('Invalid invitation');
        }
        
      } catch (err) {
        console.error('Token validation failed:', err);
        let errorMessage = 'Invalid or expired invitation link';
        
        if (err.code === 'functions/not-found') {
          errorMessage = 'This invitation link is invalid or has already been used.';
        } else if (err.code === 'functions/deadline-exceeded') {
          errorMessage = 'This invitation has expired. Please request a new invitation.';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
      } finally {
        setValidatingToken(false);
      }
    };

    if (token) {
      validateInvitation();
    } else {
      setError('No invitation token provided');
      setValidatingToken(false);
    }
  }, [token]);

  // Define steps based on role
  const getStepsForRole = (role) => {
    const baseSteps = [
      {
        id: 'welcome',
        title: 'Welcome to Block Automation',
        subtitle: 'Let\'s set up your account',
        icon: FiMail,
        component: WelcomeStep
      },
      {
        id: 'name',
        title: 'What should we call you?',
        subtitle: 'This will be displayed in the system',
        icon: FiUser,
        component: NameStep
      },
      {
        id: 'password',
        title: 'Create a secure password',
        subtitle: 'Choose a strong password for your account',
        icon: FiLock,
        component: PasswordStep
      }
    ];

    switch (role) {
      case 'administrator':
        return [
          ...baseSteps,
          {
            id: 'contact',
            title: 'Contact Information',
            subtitle: 'How can we reach you?',
            icon: FiSettings,
            component: ContactStep
          },
          {
            id: 'complete',
            title: 'Account Created!',
            subtitle: 'You\'re ready to manage the system',
            icon: FiCheck,
            component: CompleteStep
          }
        ];
        
      case 'franchisee':
        return [
          ...baseSteps,
          {
            id: 'business',
            title: 'Business Information',
            subtitle: 'Tell us about your franchise',
            icon: FiMapPin,
            component: BusinessStep
          },
          {
            id: 'timezone',
            title: 'What\'s your timezone?',
            subtitle: 'This helps us display times correctly',
            icon: FiClock,
            component: TimezoneStep
          },
          {
            id: 'contact',
            title: 'Contact Preferences',
            subtitle: 'How would you like to be contacted?',
            icon: FiSettings,
            component: ContactStep
          },
          {
            id: 'complete',
            title: 'Franchise Account Ready!',
            subtitle: 'You can now manage your blocks and operators',
            icon: FiCheck,
            component: CompleteStep
          }
        ];
        
      case 'operator':
        return [
          ...baseSteps,
          {
            id: 'contact',
            title: 'Contact Information',
            subtitle: 'How can your franchise reach you?',
            icon: FiSettings,
            component: ContactStep
          },
          {
            id: 'complete',
            title: 'Operator Account Ready!',
            subtitle: 'You can now manage assigned blocks',
            icon: FiCheck,
            component: CompleteStep
          }
        ];
        
      default:
        return baseSteps;
    }
  };

  const steps = invitation ? getStepsForRole(invitation.role) : [];
  const currentStepConfig = steps[currentStep];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const userOperations = httpsCallable(functions, 'userOperations');
      const result = await userOperations({
        operation: 'acceptInvitation',
        data: {
          token,
          password: formData.password,
          displayName: formData.displayName,
          additionalData: {
            phone: formData.phone,
            timezone: formData.timezone,
            businessName: formData.businessName,
            businessAddress: formData.businessAddress,
            preferredContact: formData.preferredContact,
            workingHours: formData.workingHours
          }
        }
      });

      if (result.data.success) {
        // Move to completion step
        nextStep();
      } else {
        throw new Error('Failed to accept invitation');
      }
    } catch (err) {
      console.error('Invitation acceptance failed:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (validatingToken) {
    return (
      <div className="invite-page">
        <div className="invite-container">
          <SpinningSun 
            fullscreen={true}
            message="Validating invitation..."
            size="extra-large"
          />
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="invite-page">
        <div className="invite-container">
          <div className="error-state">
            <h1>Invalid Invitation</h1>
            <p>{error}</p>
            <button 
              className="btn-primary"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentStepConfig) {
    return null;
  }

  const StepComponent = currentStepConfig.component;

  return (
    <div className="invite-page">
      <div className="invite-container">
        {/* Progress Bar */}
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
          <span className="progress-text">
            Step {currentStep + 1} of {steps.length}
          </span>
        </div>

        {/* Step Content */}
        <div className="step-container">
          <div className="step-header">
            <currentStepConfig.icon className="step-icon" />
            <h1 className="step-title">{currentStepConfig.title}</h1>
            <p className="step-subtitle">{currentStepConfig.subtitle}</p>
          </div>

          <div className="step-content">
            <StepComponent
              formData={formData}
              updateFormData={updateFormData}
              invitation={invitation}
              error={error}
              loading={loading}
            />
          </div>

          {/* Navigation */}
          <div className="step-navigation">
            {currentStep > 0 && (
              <button 
                className="btn-secondary"
                onClick={prevStep}
                disabled={loading}
              >
                <FiArrowLeft /> Back
              </button>
            )}
            
            {currentStep < steps.length - 1 ? (
              <button 
                className="btn-primary"
                onClick={currentStep === steps.length - 2 ? handleSubmit : nextStep}
                disabled={loading || !isStepValid()}
              >
                {loading ? (
                  <SpinningSun size="small" />
                ) : (
                  <>
                    {currentStep === steps.length - 2 ? 'Create Account' : 'Continue'}
                    <FiArrowRight />
                  </>
                )}
              </button>
            ) : (
              <button 
                className="btn-primary"
                onClick={() => navigate('/login')}
              >
                Sign In Now <FiArrowRight />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  function isStepValid() {
    switch (currentStepConfig.id) {
      case 'welcome':
        return true;
      case 'name':
        return formData.displayName.trim().length >= 2;
      case 'password':
        return formData.password.length >= 8 && formData.password === formData.confirmPassword;
      case 'business':
        return formData.businessName.trim().length >= 2;
      case 'timezone':
        return formData.timezone.length > 0;
      case 'contact':
        return formData.phone.length >= 10 || formData.preferredContact === 'email';
      default:
        return true;
    }
  }
};

// Step Components
const WelcomeStep = ({ invitation }) => (
  <div className="welcome-step">
    <div className="invitation-details">
      <p className="invitation-text">
        You've been invited to join <strong>Block Automation</strong> as a <strong>{invitation?.role}</strong>
      </p>
      {invitation?.franchiseName && (
        <p className="franchise-info">
          Franchise: <strong>{invitation.franchiseName}</strong>
        </p>
      )}
    </div>
  </div>
);

const NameStep = ({ formData, updateFormData }) => (
  <div className="name-step">
    <input
      type="text"
      placeholder="Enter your full name"
      value={formData.displayName}
      onChange={(e) => updateFormData('displayName', e.target.value)}
      className="step-input"
      autoFocus
    />
    <p className="input-help">
      This will be displayed to other users in the system
    </p>
  </div>
);

const PasswordStep = ({ formData, updateFormData }) => (
  <div className="password-step">
    <input
      type="password"
      placeholder="Enter password"
      value={formData.password}
      onChange={(e) => updateFormData('password', e.target.value)}
      className="step-input"
      autoFocus
    />
    <input
      type="password"
      placeholder="Confirm password"
      value={formData.confirmPassword}
      onChange={(e) => updateFormData('confirmPassword', e.target.value)}
      className="step-input"
    />
    <div className="password-requirements">
      <p className={formData.password.length >= 8 ? 'requirement-met' : 'requirement-unmet'}>
        ✓ At least 8 characters
      </p>
      <p className={formData.password === formData.confirmPassword && formData.password.length > 0 ? 'requirement-met' : 'requirement-unmet'}>
        ✓ Passwords match
      </p>
    </div>
  </div>
);

const BusinessStep = ({ formData, updateFormData }) => (
  <div className="business-step">
    <input
      type="text"
      placeholder="Business/Franchise Name"
      value={formData.businessName}
      onChange={(e) => updateFormData('businessName', e.target.value)}
      className="step-input"
      autoFocus
    />
    <textarea
      placeholder="Business Address"
      value={formData.businessAddress}
      onChange={(e) => updateFormData('businessAddress', e.target.value)}
      className="step-textarea"
      rows="3"
    />
  </div>
);

const TimezoneStep = ({ formData, updateFormData }) => {
  const timezones = [
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' }
  ];

  return (
    <div className="timezone-step">
      <select
        value={formData.timezone}
        onChange={(e) => updateFormData('timezone', e.target.value)}
        className="step-select"
        autoFocus
      >
        {timezones.map(tz => (
          <option key={tz.value} value={tz.value}>
            {tz.label}
          </option>
        ))}
      </select>
      <p className="input-help">
        This helps us display times and schedules correctly for your location
      </p>
    </div>
  );
};

const ContactStep = ({ formData, updateFormData }) => (
  <div className="contact-step">
    <div className="contact-method">
      <label className="radio-label">
        <input
          type="radio"
          name="preferredContact"
          value="email"
          checked={formData.preferredContact === 'email'}
          onChange={(e) => updateFormData('preferredContact', e.target.value)}
        />
        Email notifications only
      </label>
      <label className="radio-label">
        <input
          type="radio"
          name="preferredContact"
          value="both"
          checked={formData.preferredContact === 'both'}
          onChange={(e) => updateFormData('preferredContact', e.target.value)}
        />
        Email and SMS notifications
      </label>
    </div>
    
    {formData.preferredContact === 'both' && (
      <input
        type="tel"
        placeholder="Phone number"
        value={formData.phone}
        onChange={(e) => updateFormData('phone', e.target.value)}
        className="step-input"
      />
    )}
    
    <div className="working-hours">
      <h4>Preferred contact hours</h4>
      <div className="time-inputs">
        <label>
          From
          <input
            type="time"
            value={formData.workingHours.start}
            onChange={(e) => updateFormData('workingHours', {
              ...formData.workingHours,
              start: e.target.value
            })}
            className="time-input"
          />
        </label>
        <label>
          To
          <input
            type="time"
            value={formData.workingHours.end}
            onChange={(e) => updateFormData('workingHours', {
              ...formData.workingHours,
              end: e.target.value
            })}
            className="time-input"
          />
        </label>
      </div>
    </div>
  </div>
);

const CompleteStep = ({ invitation }) => (
  <div className="complete-step">
    <div className="success-icon">
      <FiCheck />
    </div>
    <h2>Welcome to Block Automation!</h2>
    <p>
      Your {invitation?.role} account has been successfully created.
    </p>
    <div className="next-steps">
      <h4>What's next?</h4>
      <ul>
        <li>Sign in with your credentials</li>
        <li>Explore the dashboard</li>
        <li>Start managing your blocks</li>
      </ul>
    </div>
  </div>
);

export default InviteAcceptance; 